import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import { useAuth } from "../../utils/AuthContext";
import { PrintBillLayout } from "../PrintBillLayout";
import axios from "axios";
import { saveToCache, readFromCache } from "../../services/pathologyCache";
import { addToNicuOutbox, saveNicuLocal, generateClientId } from "../../services/nicuOutbox";
import CreatableSelect from "react-select/creatable";
import NicuServiceModal from "./NicuConfig/ServiceList/NicuServiceModal";
import NicuAgentModal from "./NicuConfig/NicuAgent/NicuAgentModal";
import NicuBedModal from "./NicuConfig/BedAllocation/NicuBedModal";
import NicuPrintLayout from "../NicuPrintLayout";
import { getDistrictOptions, getDivisionOptions, getUpazilaOptions } from "../../data/bdLocations/bdLocations";

// API Configurations
const api = axios.create({
  baseURL: "https://api.muktihospital.com/api/nicu",
  headers: {
    "x-api-key":
      "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
  },
});

const GENERAL_API_URL = "https://api.muktihospital.com/api";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const NicuAdmission = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const editAdmissionData = location.state?.editAdmissionData || null;
  const isEditMode = !!editAdmissionData;
  console.log("EDIT DATA >>>", editAdmissionData);   // TEMP DEBUG

  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin";
  const canEditDate = isAdmin || isSuperAdmin;

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "NICU" },
    { label: isEditMode ? "Edit Admission" : "Admission" },
  ];

  const parseDate = (dateInput) => {
    if (!dateInput) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    try {
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

      if (isNaN(date.getTime())) {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch {
      console.warn("Invalid date format:", dateInput);
      return new Date().toISOString().split("T")[0];
    }
  };

  const [formData, setFormData] = useState({
    id: editAdmissionData?.id || "",
    serviceType: editAdmissionData?.serviceType || "",
    patient: editAdmissionData?.patientName || "",
    fathersName: editAdmissionData?.fatherName || "",
    mothersName: editAdmissionData?.motherName || "",
    address: editAdmissionData?.address || "",
    division: editAdmissionData?.division || "",
    district: editAdmissionData?.district || "",
    upazila: editAdmissionData?.upazila || "",
    relativeName: editAdmissionData?.relativeName || "",
    co: editAdmissionData?.careOf || "",
    age: editAdmissionData?.age || "",
    sex: editAdmissionData?.sex || "",
    religion: editAdmissionData?.religion || "",
    dateOfBirth: parseDate(editAdmissionData?.dateOfBirth) || "",
    phoneMobile: editAdmissionData?.phoneNumber || "",
    admissionDate: editAdmissionData?.admissionDate
      ? parseDate(editAdmissionData.admissionDate)
      : parseDate(new Date()),
    admissionTime: editAdmissionData?.admissionTime
      ? editAdmissionData.admissionTime.replace(/\+06$/, "")
      : new Date().toLocaleTimeString("en-US", {
          hour12: true,
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Dhaka",
        }),
    relativeMobileNo: editAdmissionData?.relativeMobileNumber || "",
    relationship: editAdmissionData?.relationship || "",
    consultantCode: editAdmissionData?.consultantCode || "",
    consultantName: editAdmissionData?.consultantName || "",
    agent: editAdmissionData?.agentName || "",
    agentName: editAdmissionData?.agentName || "",
    bedNo: editAdmissionData?.bedNo || "",
    employeeCode: editAdmissionData?.employeeCode || user?.id || "1001",
    employeeName:
      editAdmissionData?.employeeName || user?.name || "Md Taiful Islam",
    dischargeCode: editAdmissionData?.dischargeCode || "",
    billingType: editAdmissionData?.billingType || "Normal",
    status: editAdmissionData?.status || "Admitted",
    isDateChangeRequested: editAdmissionData?.isDateChangeRequested || false,
    requestedDate: editAdmissionData?.requestedDate
      ? parseDate(editAdmissionData.requestedDate)
      : "",
    requestedTime: editAdmissionData?.requestedTime || "",
  });

  const [selectedService, setSelectedService] = useState(null);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);
  const [selectedBillingType, setSelectedBillingType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isNewServiceModalOpen, setIsNewServiceModalOpen] = useState(false);
  const [isNewAgentModalOpen, setIsNewAgentModalOpen] = useState(false);
  const [isNewBedModalOpen, setIsNewBedModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);

  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatientOption, setSelectedPatientOption] = useState(null);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [consultantOptions, setConsultantOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);
  const [bedData, setBedData] = useState([]);
  const [allBedOptions, setAllBedOptions] = useState([]);
  const [occupiedBeds, setOccupiedBeds] = useState([]);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);

  const billingTypeOptions = [
    { value: "Normal", label: "Normal" },
    { value: "Insurance", label: "Insurance" },
    { value: "Government", label: "Government" },
  ];

  const statusOptions = [
    { value: "Admitted", label: "Admitted" },
    { value: "Released", label: "Released" },
  ];

  // Fetch dropdown helper
  const fetchData = async (
    endpoint,
    setOptions,
    mapResponse,
    additionalState = null,
  ) => {
    try {
      const response = await api.get(`/${endpoint}`);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data[endpoint + "s"] ||
          response.data[endpoint] ||
          response.data.data ||
          response.data.results ||
          response.data.items ||
          [];
      // console.log(`${endpoint} extracted data:`, data);
      if (!Array.isArray(data)) {
        console.error(
          `Invalid ${endpoint} response: Expected array, got`,
          data,
        );
        toast.error(`Invalid ${endpoint} data format: Expected array.`, {
          position: "top-right",
          autoClose: 3000,
        });
        setOptions([]);
        return;
      }
      const options = data.map((item, index) => {
        const mapped = mapResponse(item);
        return mapped;
      });
      setOptions(options);
      if (additionalState) additionalState(data);

      saveToCache(`nicu_form_${endpoint}`, data);
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      if (error.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(`nicu_form_${endpoint}`);
        if (cached && Array.isArray(cached)) {
          const options = cached.map((item) => mapResponse(item));
          setOptions(options);
          if (additionalState) additionalState(cached);
        } else {
          setOptions([]);
        }
      } else {
        toast.error(`Failed to fetch ${endpoint}: ${error.message}`, {
          position: "top-right",
          autoClose: 3000,
        });
        setOptions([]);
      }
    }
  };

  // --- FETCH ALL DATA ---
  useEffect(() => {
    setIsLoading(true);

    const nicuDataPromise = Promise.all([
      fetchData("service", setServiceOptions, (service) => ({
        value: service.id || service.serviceId || service.code,
        label: service.serviceName || service.name,
      })),
      fetchData("agent", setAgentOptions, (agent) => ({
        value: agent.agentCode || agent.id || agent.code,
        label: agent.agentName || agent.name,
      })),
      fetchData(
        "bed",
        setAllBedOptions,
        (bed) => ({
          value: bed.bedNumber || bed.BED_NUMBER || bed.number,
          label: bed.bedNumber || bed.BED_NUMBER || bed.number,
        }),
        setBedData,
      ),
      api
        .get("/admission/occupied-beds")
        .then((res) => {
          const ids = res.data.occupiedBedIds || [];
          setOccupiedBeds(ids);
          saveToCache("nicu_form_occupied_beds", ids);
        })
        .catch(async (err) => {
          console.error("Error fetching occupied beds", err);
          if (err.code === "ERR_NETWORK" || !navigator.onLine) {
            const cached = await readFromCache("nicu_form_occupied_beds");
            if (cached && Array.isArray(cached)) setOccupiedBeds(cached);
          }
        }),
    ]);

    const doctorPromise = axios
      .get(`${GENERAL_API_URL}/doctor`, {
        headers: { "x-api-key": API_KEY },
      })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        const options = data.map((d) => {
          let name = d.name || "Unknown";
          if (d.translations) {
            try {
              const t =
                typeof d.translations === "string"
                  ? JSON.parse(d.translations)
                  : d.translations;
              name = t?.en?.name || t?.bn?.name || name;
            } catch (e) {}
          }
          return { value: d.id, label: name };
        });
        setConsultantOptions(options);
        saveToCache("nicu_form_doctors", options);
      })
      .catch(async (err) => {
        console.error("Error fetching doctors:", err);
        if (err.code === "ERR_NETWORK" || !navigator.onLine) {
          const cached = await readFromCache("nicu_form_doctors");
          if (cached && Array.isArray(cached)) {
            setConsultantOptions(cached);
            return;
          }
        }
        toast.error("Failed to load doctor list.");
      });

    // 3. Fetch Patients
    const patientDataPromise = axios
      .get(`${GENERAL_API_URL}/patient`, {
        headers: { "x-api-key": API_KEY },
      })
      .then((response) => {
        const formattedPatients = response.data.map((patient) => ({
          value: patient.id,
          label: `${patient.name} (${patient.phoneNumber || "No Phone"})`,
          nameOnly: patient.name,
          phone: patient.phoneNumber || "",
        }));
        setPatientOptions(formattedPatients);
        saveToCache("nicu_form_patients", formattedPatients);
      })
      .catch(async (error) => {
        console.error("Error fetching patients:", error);
        if (error.code === "ERR_NETWORK" || !navigator.onLine) {
          const cached = await readFromCache("nicu_form_patients");
          if (cached && Array.isArray(cached)) {
            setPatientOptions(cached);
            return;
          }
        }
        toast.error("Failed to load patient list.");
      });

    Promise.all([nicuDataPromise, doctorPromise, patientDataPromise]).finally(() =>
      setIsLoading(false),
    );
  }, []);

  // Filter Beds Logic
  useEffect(() => {
    if (allBedOptions.length > 0) {
      const filteredBeds = allBedOptions.filter((bed) => {
        const isOccupied = occupiedBeds.includes(bed.value);
        if (!isOccupied) return true;
        if (isEditMode && formData.bedNo === bed.value) return true;
        return false;
      });
      setBedOptions(filteredBeds);
    }
  }, [allBedOptions, occupiedBeds, isEditMode, formData.bedNo]);

  // Set initial selections for edit mode
  useEffect(() => {
    if (
      isEditMode &&
      serviceOptions.length &&
      consultantOptions.length &&
      agentOptions.length &&
      bedOptions.length
    ) {
      const serviceMatch = serviceOptions.find(
        (opt) => opt.value === formData.serviceType,
      );
      setSelectedService(serviceMatch || null);

      const patientMatch = patientOptions.find(
        (p) => p.nameOnly === formData.patient,
      ) || { value: "manual", label: formData.patient };
      setSelectedPatientOption(patientMatch);

      // Match Doctor
      let consultantMatch = consultantOptions.find(
        (opt) => opt.value === formData.consultantCode,
      );
      if (!consultantMatch) {
        consultantMatch = consultantOptions.find(
          (opt) => opt.label === formData.consultantName,
        );
      }
      setSelectedConsultant(consultantMatch || null);

      const agentMatch = agentOptions.find(
        (opt) => opt.label === formData.agentName,
      );
      setSelectedAgent(agentMatch || null);

      const bedMatch = bedOptions.find((opt) => opt.value === formData.bedNo);
      setSelectedBed(bedMatch || null);

      setSelectedBillingType(
        billingTypeOptions.find((opt) => opt.value === formData.billingType) ||
          null,
      );
      setSelectedStatus(
        statusOptions.find((opt) => opt.value === formData.status) || null,
      );
    } else {
      setSelectedBillingType(
        billingTypeOptions.find((opt) => opt.value === "Normal") || null,
      );
      setSelectedStatus(
        statusOptions.find((opt) => opt.value === "Admitted") || null,
      );
    }
  }, [
    isEditMode,
    serviceOptions,
    consultantOptions,
    agentOptions,
    bedOptions,
    formData,
    patientOptions,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (field, option) => {
    setFormData((prevData) => {
      let newData = { ...prevData, [field]: option?.value || "" };

      if (field === "bedNo" && option) {
        const selectedBedData = bedData.find(
          (bed) => bed.bedNumber === option.value,
        );
        newData.wardCabin = selectedBedData?.wcCode || "";
      }

      if (field === "consultantCode" && option) {
        newData.consultantCode = option.value;
        newData.consultantName = option.label;
      }

      if (field === "agent" && option) {
        newData.agent = option.value;
        newData.agentName = option.label;
      }
      return newData;
    });

    const setters = {
      serviceType: setSelectedService,
      consultantCode: setSelectedConsultant,
      agent: setSelectedAgent,
      bedNo: setSelectedBed,
      billingType: setSelectedBillingType,
      status: setSelectedStatus,
    };
    setters[field](option);
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingAction('save');

    const requiredFields = ["patient", "phoneMobile", "consultantName", "agentName", "bedNo"];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(", ")}`);
      setIsLoading(false);
      return;
    }

    const payload = {
      ...formData,

      // Manual Mapping
      patientName: formData.patient,
      phoneNumber: formData.phoneMobile,
      patientId: formData.patientId || null,
      fatherName: formData.fathersName,
      motherName: formData.mothersName,
      careOf: formData.co,
      dateOfBirth: formData.dateOfBirth || null,
      admissionDate: formData.admissionDate || null,
      isDateChangeRequested: formData.isDateChangeRequested || false,
      requestedDate: formData.isDateChangeRequested ? formData.requestedDate || null : null,
      requestedTime: formData.isDateChangeRequested ? formData.requestedTime || null : null,
    };

    try {
      if (isEditMode) {
        await api.put(`/admission/edit/${formData.id}`, payload);
        toast.success("Admission updated successfully");
      } else {
        const clientId = payload.clientId || generateClientId();
        payload.clientId = clientId;
        await api.post("/admission/add", payload);
        toast.success("Admission saved successfully");
        resetForm();
      }
    } catch (error) {
      console.error("Error saving admission:", error);
      console.log("OFFLINE CHECK →", { code: error.code, online: navigator.onLine, isEditMode });
      if ((error.code === "ERR_NETWORK" || !navigator.onLine) && !isEditMode) {
        console.log("OFFLINE BRANCH ENTERED");
        const clientId = payload.clientId || generateClientId();
        payload.clientId = clientId;
        const provisionalNo = `OFFLINE-${clientId.slice(0, 8)}`;
        const queued = await addToNicuOutbox("admission", payload, provisionalNo);
        if (queued) {
          await saveNicuLocal({
            clientId,
            type: "admission",
            provisionalNo,
            patientName: payload.patientName,
            bedNo: payload.bedNo,
            phoneNumber: payload.phoneNumber,
            admissionDate: payload.admissionDate,
            consultantName: payload.consultantName,
            agentName: payload.agentName,
            status: "Admitted",
            pendingSync: true,
            createdAt: new Date().toISOString(),
          });
          toast.info(`Saved offline (${provisionalNo}). It will sync when you're back online.`);
          resetForm();
        } else {
          console.log("QUEUED WAS NULL — addToNicuOutbox failed");
          toast.error("Could not save offline. Please try again.");
        }
      } else {
        const serverError = error.response?.data?.error || error.message;
        toast.error(`Failed to save: ${serverError}`);
      }
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

 const handleSaveAndPrint = async () => {
    setIsLoading(true);
    setLoadingAction('print');

    const requiredFields = ["patient", "phoneMobile", "consultantName", "agentName", "bedNo"];
    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0) {
      toast.error(`Missing required fields: ${missingFields.join(", ")}`);
      setIsLoading(false);
      return;
    }

    const payload = {
      ...formData,
      patientName: formData.patient,
      phoneNumber: formData.phoneMobile,
      patientId: formData.patientId || null,
      fatherName: formData.fathersName,
      motherName: formData.mothersName,
      careOf: formData.co,
      dateOfBirth: formData.dateOfBirth || null,
      admissionDate: formData.admissionDate || null,
      isDateChangeRequested: formData.isDateChangeRequested || false,
      requestedDate: formData.isDateChangeRequested ? formData.requestedDate || null : null,
      requestedTime: formData.isDateChangeRequested ? formData.requestedTime || null : null,
    };

    try {
      let response;
      if (isEditMode) {
        response = await api.put(`/admission/edit/${formData.id}`, payload);
      } else {
        const clientId = payload.clientId || generateClientId();
        payload.clientId = clientId;
        response = await api.post("/admission/add", payload);
      }

      const billInfo = (
        <div className="w-full font-sans text-gray-800">
          {/* --- TOP: NAME + REG NO --- */}
          <div className="flex justify-between items-end border-b-2 border-gray-800 pb-3 mb-5">
            <div className="pr-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
                Patient Name
              </p>
              <h2 className="text-2xl font-extrabold text-gray-900 uppercase leading-none whitespace-nowrap">
                {formData.patient || "Unknown Patient"}
              </h2>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
                Registration No
              </p>
              <h2 className="text-2xl font-extrabold text-gray-900 leading-none whitespace-nowrap">
                {response.data.admission?.regNo || "N/A"}
              </h2>
            </div>
          </div>

          {/* --- SECTION 1: PATIENT & GUARDIAN --- */}
          <div className="mb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider border-b border-gray-400 pb-1 mb-3">
              Patient &amp; Guardian Information
            </h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-base">
              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Age / Sex: </span>
                <span className="font-bold whitespace-nowrap">
                  {formData.age || "--"} / {formData.sex || "--"}
                </span>
              </div>
              <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold">Father: </span>
                <span className="font-bold whitespace-nowrap">{formData.fathersName || "--"}</span>
              </div>

              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Date of Birth: </span>
                <span className="font-bold whitespace-nowrap">
                  {formData.dateOfBirth
                    ? new Date(formData.dateOfBirth).toLocaleDateString("en-GB")
                    : "--"}
                </span>
              </div>
              <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold">Mother:</span>
                <span className="font-bold whitespace-nowrap">{formData.mothersName || "--"}</span>
              </div>

              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Religion: </span>
                <span className="font-bold whitespace-nowrap">{formData.religion || "--"}</span>
              </div>
              <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold">Guardian: </span>
                <span className="font-bold whitespace-nowrap">{formData.relativeName || "--"}</span>
              </div>

              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Mobile: </span>
                <span className="font-bold whitespace-nowrap">{formData.phoneMobile || "--"}</span>
              </div>
              <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Contact Person: </span>
                <span className="font-bold whitespace-nowrap">
                  {formData.agentName || selectedAgent?.label || "--"}
                </span>
              </div>

              {/* full width — never breaks */}
              <div className="col-span-2 flex items-baseline pt-1">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Full Address: </span>
                <span className="font-bold">
                  {[formData.address, formData.upazila, formData.district, formData.division]
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                  {formData.co ? ` (C/O: ${formData.co})` : ""}
                </span>
              </div>
            </div>
          </div>

          {/* --- SECTION 2: ADMISSION DETAILS --- */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider border-b border-gray-400 pb-1 mb-3">
              Admission Details
            </h3>
            <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-base">
              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Admit Date: </span>
                <span className="font-bold whitespace-nowrap">{formData.admissionDate || "--"}</span>
              </div>
              <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold">Status: </span>
                <span className="font-bold whitespace-nowrap">{formData.status || "--"}</span>
              </div>

              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Admit Time: </span>
                <span className="font-bold whitespace-nowrap">{formData.admissionTime || "--"}</span>
              </div>
              <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold">Bill Type: </span>
                <span className="font-bold whitespace-nowrap">{formData.billingType || "Normal"}</span>
              </div>

              <div className="flex items-baseline">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Bed No: </span>
                <span className="font-bold whitespace-nowrap border border-gray-400 px-2">
                  {selectedBed?.label || "None"}
                </span>
              </div>
              <div></div>

              {/* full width — long name never breaks */}
              <div className="col-span-2 flex items-baseline pt-1">
                <span className="w-28 shrink-0 text-gray-500 font-semibold">Consultant: </span>
                <span className="font-bold whitespace-nowrap">{formData.consultantName || "N/A"}</span>
              </div>
            </div>
          </div>

          {/* --- METADATA --- */}
          <div className="mt-6 pt-2 border-t border-dashed border-gray-300 flex justify-between text-sm text-gray-500">
            <span>Entry By: {formData.employeeName} ({formData.employeeCode})</span>
            <span>Printed: {new Date().toLocaleString("en-GB")}</span>
          </div>
        </div>
      );

      const newPrintData = { billInfo };
      setPrintData(newPrintData);
      setShowPrintPreview(true);
      toast.success(
        `${isEditMode ? "Admission updated" : "Admission saved"} and print preview triggered!`,
        {
          position: "top-right",
          autoClose: 5000,
        },
      );
      setTimeout(() => {
        window.print();
        setShowPrintPreview(false);
        resetForm();
        navigate("/nicu-patients");
      }, 1500);
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "saving"} and printing admission:`,
        error,
      );
      if ((error.code === "ERR_NETWORK" || !navigator.onLine) && !isEditMode) {
        const clientId = payload.clientId || generateClientId();
        payload.clientId = clientId;
        const provisionalNo = `OFFLINE-${clientId.slice(0, 8)}`;
        const queued = await addToNicuOutbox("admission", payload, provisionalNo);
        if (queued) {
          await saveNicuLocal({
            clientId,
            type: "admission",
            provisionalNo,
            patientName: payload.patientName,
            bedNo: payload.bedNo,
            phoneNumber: payload.phoneNumber,
            admissionDate: payload.admissionDate,
            consultantName: payload.consultantName,
            agentName: payload.agentName,
            status: "Admitted",
            pendingSync: true,
            createdAt: new Date().toISOString(),
          });
          toast.info(`Saved offline (${provisionalNo}). It will sync when you're back online.`);
          resetForm();
        } else {
          toast.error("Could not save offline. Please try again.");
        }
      } else {
        toast.error(
          `Failed to ${isEditMode ? "update" : "save"} and print admission: ${error.response?.data?.error || error.message}`,
          {
            position: "top-right",
            autoClose: 5000,
          },
        );
      }
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      serviceType: "",
      patient: "",
      fathersName: "",
      mothersName: "",
      address: "",
      division: "",
      district: "",
      upazila: "",
      relativeName: "",
      co: "",
      age: "",
      sex: "",
      religion: "",
      dateOfBirth: "",
      phoneMobile: "",
      admissionDate: new Date().toLocaleDateString("en-GB"),
      admissionTime:
        new Date().toLocaleTimeString("en-US", {
          hour12: true,
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Dhaka",
        }) + " +06",
      relativeMobileNo: "",
      relationship: "",
      consultantCode: "",
      consultantName: "",
      agent: "",
      agentName: "",
      bedNo: "",
      primaryDiagnosis: "",
      employeeCode: user?.id || "1001",
      employeeName: user?.name || "Md Taiful Islam",
      dischargeCode: "",
      billingType: "Normal",
      status: "Admitted",
      patientId: "",
      isDateChangeRequested: false,
      requestedDate: "",
      requestedTime: "",
    });
    setSelectedPatientOption(null);
    setSelectedService(null);
    setSelectedConsultant(null);
    setSelectedAgent(null);
    setSelectedBed(null);
    setSelectedBillingType(null);
    setSelectedStatus(null);
    setShowPrintPreview(false);
    setPrintData(null);
  };

  const onClose = () => {
    resetForm();
    navigate("/nicu-patients");
  };

  const handleAddNewService = () => setIsNewServiceModalOpen(true);
  const handleAddNewAgent = () => setIsNewAgentModalOpen(true);
  const handleAddNewBed = () => setIsNewBedModalOpen(true);

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;

    const el = e.target;
    if (el.tagName === "TEXTAREA" || el.tagName === "BUTTON") return;
    if (el.getAttribute("role") === "combobox" || el.closest(".react-select-container")) return;

    e.preventDefault();
    const fields = Array.from(
      e.currentTarget.querySelectorAll("input, select, textarea")
    ).filter((f) => !f.disabled && !f.readOnly && f.type !== "hidden" && f.offsetParent !== null);

    const next = fields[fields.indexOf(el) + 1];
    if (next) { next.focus(); if (next.select) next.select(); }
  };

  useEffect(() => {
    if (!isEditMode || !editAdmissionData?.id) return;
    (async () => {
      try {
        const res = await api.get(`/admission/${editAdmissionData.id}`);
        const full = res.data.admission || res.data;
        if (!full) return;
        setFormData((prev) => ({
          ...prev,
          patientId: full.patientId || prev.patientId || "",
          consultantCode: full.consultantCode || prev.consultantCode || "",
          isDateChangeRequested: full.isDateChangeRequested || false,
          requestedDate: full.requestedDate ? parseDate(full.requestedDate) : "",
          requestedTime: full.requestedTime || "",
          division: full.division || prev.division || "",
          district: full.district || prev.district || "",
          upazila: full.upazila || prev.upazila || "",
          address: full.address ?? prev.address ?? "",
        }));
      } catch (err) {
        console.error("Failed to load the full admission record:", err);
      }
    })();
  }, [isEditMode, editAdmissionData?.id]);

  useEffect(() => {
    if (!isEditMode || !editAdmissionData) return;
    const savedName = editAdmissionData.patientName || "";
    if (!savedName) return;
    const matched = patientOptions.find(
      (opt) => opt.value === editAdmissionData.patientId || opt.nameOnly === savedName
    );
    setSelectedPatientOption(matched || { value: editAdmissionData.patientId || savedName, label: savedName, nameOnly: savedName });
  }, [isEditMode, editAdmissionData, patientOptions]);

  const handlePatientSelectChange = (selectedOption) => {
    setSelectedPatientOption(selectedOption);
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        patient: selectedOption.nameOnly || selectedOption.label,
        phoneMobile: selectedOption.phone || prev.phoneMobile,
        address: selectedOption.address || prev.address,
        age: selectedOption.age || prev.age,
        sex: selectedOption.sex || prev.sex,
        patientId: String(selectedOption.value).startsWith("new_") ? "" : selectedOption.value,
      }));
    } else {
      setFormData((prev) => ({ ...prev, patient: "", patientId: "" }));
    }
  };

  const handleCreatePatient = (inputValue) => {
    const newOption = {
      value: `new_${Date.now()}`,
      label: inputValue,
      nameOnly: inputValue,
      phone: "",
    };

    setSelectedPatientOption(newOption);
    setPatientOptions((prev) => [...prev, newOption]);

    setFormData((prev) => ({
      ...prev,
      patient: inputValue,
      phoneMobile: "",
    }));

    toast.info(`New patient "${inputValue}" added. Please fill in details.`);
  };

  const handleSaveNewService = (savedService, isEditMode) => {
    if (!isEditMode) {
      const newService = {
        value: savedService.id,
        label: savedService.serviceName,
      };
      setServiceOptions((prev) => [...prev, newService]);
      setFormData((prevData) => ({
        ...prevData,
        serviceType: newService.value,
      }));
      setSelectedService(newService);
      toast.success("Service added and selected!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsNewServiceModalOpen(false);
  };

  const handleSaveNewAgent = (savedAgent, isEditMode) => {
    if (!isEditMode) {
      const newAgent = {
        value: savedAgent.agentCode || savedAgent.id || savedAgent.code,
        label: savedAgent.agentName || savedAgent.name,
      };
      setAgentOptions((prev) => [...prev, newAgent]);
      setFormData((prevData) => ({
        ...prevData,
        agent: newAgent.value,
        agentName: newAgent.label,
      }));
      setSelectedAgent(newAgent);
      toast.success("Agent added and selected!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsNewAgentModalOpen(false);
  };

  const handleSaveNewBed = (savedBed, isEditMode) => {
    if (!isEditMode) {
      const newBed = {
        value: savedBed.id,
        label: savedBed.bedNumber || savedBed.bedNo || savedBed.number,
      };
      setAllBedOptions((prev) => [...prev, newBed]);

      setBedOptions((prev) => [...prev, newBed]);

      setBedData((prev) => [...prev, savedBed]);

      setSelectedBed(newBed);
      setFormData((prevData) => ({ ...prevData, bedNo: newBed.value }));

      toast.success("Bed added and selected!", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setIsNewBedModalOpen(false);
  };

  return (
    <div>
      <ToastContainer />
      <PageHeading
        title={isEditMode ? "Edit Admission" : "Admission"}
        breadcrumbs={breadcrumbs}
      />
      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* LEFT COLUMN: PATIENT INFO */}
          <div className="bg-white shadow-lg rounded-lg">
            <div className="border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="font-medium text-gray-700">Registration Form</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              <div>
                <label className="label">
                  Patient's Name: <span className="text-red-600">*</span>
                </label>
                <CreatableSelect
                  isClearable
                  isSearchable
                  options={patientOptions}
                  value={selectedPatientOption}
                  onChange={handlePatientSelectChange}
                  onCreateOption={handleCreatePatient}
                  placeholder="Select or Type New Patient Name"
                  /* The picked name stays as editable text in the same box:
                     the value "pill" is hidden and the input mirrors formData.patient,
                     so a selected name can still be corrected by typing. */
                  controlShouldRenderValue={false}
                  inputValue={formData.patient || ""}
                  onInputChange={(val, meta) => {
                    if (meta.action === "input-change") {
                      setFormData((prev) => ({ ...prev, patient: val }));
                    }
                  }}
                  formatCreateLabel={(inputValue) =>
                    `Add new patient: "${inputValue}"`
                  }
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: "#e2e8f0",
                      fontSize: "14px",
                      backgroundColor: "#f7fafc",
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontSize: "14px",
                      backgroundColor: state.isSelected
                        ? "#3b82f6"
                        : state.isFocused
                          ? "#ebf8ff"
                          : "transparent",
                      color: state.isSelected ? "#ffffff" : "#2d3748",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: "#ffffff",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      padding: "0px",
                    }),
                  }}
                />
                {/* Hidden input to ensure HTML5 'required' validation works */}
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ opacity: 0, height: 0, position: "absolute" }}
                  value={formData.patient}
                  required
                  onChange={() => {}}
                />
              </div>
              <div>
                <label className="label">Father's / Husband's Name:</label>
                <input
                  type="text"
                  name="fathersName"
                  value={formData.fathersName}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="mothersName" className="label">
                  Mother's Name:
                </label>
                <input
                  type="text"
                  id="mothersName"
                  name="mothersName"
                  value={formData.mothersName}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="relativeName" className="label">
                  Relative Name:
                </label>
                <input
                  type="text"
                  id="relativeName"
                  name="relativeName"
                  value={formData.relativeName}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="co" className="label">
                  C/O:
                </label>
                <input
                  type="text"
                  id="co"
                  name="co"
                  value={formData.co}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="age" className="label">
                  Age:
                </label>
                <input
                  type="text"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="sex" className="label">
                  Sex:
                </label>
                <input
                  type="text"
                  id="sex"
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="religion" className="label">
                  Religion:
                </label>
                <input
                  type="text"
                  id="religion"
                  name="religion"
                  value={formData.religion}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">Division:</label>
                <DynamicSelect
                  options={getDivisionOptions()}
                  value={formData.division ? { value: formData.division, label: formData.division } : null}
                  onChange={(opt) =>
                    setFormData((prev) => ({
                      ...prev,
                      division: opt?.value || "",
                      district: "",
                      upazila: "",
                    }))
                  }
                  isSearchable
                  isClearable
                  placeholder="Select Division"
                />
              </div>
              <div>
                <label className="label">District:</label>
                <DynamicSelect
                  options={getDistrictOptions(formData.division)}
                  value={formData.district ? { value: formData.district, label: formData.district } : null}
                  onChange={(opt) =>
                    setFormData((prev) => ({
                      ...prev,
                      district: opt?.value || "",
                      upazila: "",
                    }))
                  }
                  isSearchable
                  isClearable
                  placeholder="Select District"
                />
              </div>
              <div>
                <label className="label">Upazila:</label>
                <DynamicSelect
                  options={getUpazilaOptions(formData.district)}
                  value={formData.upazila ? { value: formData.upazila, label: formData.upazila } : null}
                  onChange={(opt) => setFormData((prev) => ({ ...prev, upazila: opt?.value || "" }))}
                  isSearchable
                  isClearable
                  placeholder={formData.district ? "Select Upazila" : "Select a district first"}
                  isDisabled={!formData.district}
                />
              </div>
              <div>
                <label htmlFor="address" className="label">
                  Address (Village / Road):
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              <div>
                <label className="label">Date of Birth:</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">
                  Phone <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="phoneMobile"
                  value={formData.phoneMobile}
                  onChange={handleChange}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="label">Admission Date:</label>
                <input
                  type="date"
                  name="admissionDate"
                  value={formData.admissionDate}
                  onChange={handleChange}
                  className={`input-field ${!canEditDate ? "bg-gray-100" : ""}`}
                  readOnly={!canEditDate}
                />
              </div>
              <div>
                <label className="label">Admission Time:</label>
                <input
                  type="text"
                  name="admissionTime"
                  value={formData.admissionTime}
                  onChange={handleChange}
                  className={`input-field ${!canEditDate ? "bg-gray-100" : ""}`}
                  readOnly={!canEditDate}
                />
              </div>
              {/* === DATE/TIME LOGIC END === */}
              <div>
                <label htmlFor="relativeMobileNo" className="label">
                  Relative Mobile No:
                </label>
                <input
                  type="text"
                  id="relativeMobileNo"
                  name="relativeMobileNo"
                  value={formData.relativeMobileNo}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              {(!canEditDate || isEditMode) && (
                <div className="md:col-span-2 flex items-center gap-2 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <input
                    type="checkbox"
                    id="isDateChangeRequested"
                    name="isDateChangeRequested"
                    checked={formData.isDateChangeRequested}
                    onChange={handleChange}
                    className="w-4 h-4 text-M-primary-color border-gray-300 rounded focus:ring-M-primary-color"
                  />
                  <label
                    htmlFor="isDateChangeRequested"
                    className="text-sm text-gray-700 font-medium cursor-pointer"
                  >
                    Request Date/Time Correction (Backdate)
                    <span className="block text-xs text-gray-500 font-normal">
                      Check this to notify Admin that the admission date needs
                      adjustment.
                    </span>
                  </label>
                </div>
              )}

              {/* Backdate values — only filled in when a correction is actually requested */}
              {formData.isDateChangeRequested && (
                <>
                  <div>
                    <label className="label">
                      Backdate: <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="date"
                      name="requestedDate"
                      value={formData.requestedDate}
                      onChange={handleChange}
                      max={new Date().toISOString().split("T")[0]}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="label">
                      Backdate Time: <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="time"
                      name="requestedTime"
                      value={formData.requestedTime}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                </>
              )}

              {/* Warning for Admins */}
              {canEditDate && formData.isDateChangeRequested && (
                <div className="md:col-span-2 p-3 bg-red-100 text-red-700 text-sm font-bold rounded border border-red-200 flex flex-col gap-2">
                  <span>
                    ⚠️ This record has a Backdate Request. Please verify and
                    update the Date/Time.
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="clearFlag"
                      // Unchecking this means we clear the request (set to false)
                      checked={!formData.isDateChangeRequested}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isDateChangeRequested: !e.target.checked,
                        }))
                      }
                      className="w-4 h-4"
                    />
                    <label
                      htmlFor="clearFlag"
                      className="font-normal text-black cursor-pointer"
                    >
                      Mark Request as Resolved (Uncheck "Request Active")
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: DOCTOR & BED INFO */}
          <div className="bg-white shadow-lg rounded-lg">
            <div className="border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="font-medium text-gray-700">Others</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              <div>
                <label htmlFor="consultantCode" className="label">
                  Consultant Name: <span className="text-red-600">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={consultantOptions}
                      value={selectedConsultant}
                      onChange={(opt) =>
                        handleSelectChange("consultantCode", opt)
                      }
                      isSearchable
                      placeholder="Search Doctor..."
                      isDisabled={isLoading}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* AGENT */}
              <div>
                <label htmlFor="agent" className="label">
                  Agent: <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={agentOptions}
                      value={selectedAgent}
                      onChange={(opt) => handleSelectChange("agent", opt)}
                      isSearchable
                      placeholder="Search Agent"
                      isDisabled={isLoading}
                      required
                    />
                  </div>
                  <button
                    onClick={handleAddNewAgent}
                    className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                    type="button"
                  >
                    Add New
                  </button>
                </div>
              </div>

              {/* BED */}
              <div>
                <label className="label">
                  Bed No: <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={bedOptions}
                      value={selectedBed}
                      onChange={(opt) => handleSelectChange("bedNo", opt)}
                      isSearchable
                      placeholder="Search Bed"
                      required
                    />
                  </div>
                  <button
                    onClick={handleAddNewBed}
                    className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
                    type="button"
                  >
                    Add New
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="employeeCode" className="label">
                  Employee Code:
                </label>
                <input
                  type="text"
                  id="employeeCode"
                  name="employeeCode"
                  value={formData.employeeCode}
                  onChange={handleChange}
                  className="input-field bg-gray-100"
                  disabled
                />
              </div>

              <div>
                <label className="label">Employee Name:</label>
                <input
                  type="text"
                  value={formData.employeeName}
                  className="input-field bg-gray-100"
                  disabled
                />
              </div>
              <div>
                <label className="label">Discharge Code:</label>
                <input
                  type="text"
                  id="dischargeCode"
                  name="dischargeCode"
                  value={formData.dischargeCode}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              <div>
                <label className="label">Billing Type:</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <DynamicSelect
                      options={billingTypeOptions}
                      value={selectedBillingType}
                      onChange={(option) =>
                        handleSelectChange("billingType", option)
                      }
                      isSearchable
                      placeholder="Search Billing Type"
                      isDisabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="label">Status:</label>
                <DynamicSelect
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(opt) => handleSelectChange("status", opt)}
                  placeholder="Select Status"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER BUTTONS */}
        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={resetForm}
            type="button"
            className="bg-gray-600 text-white py-2 px-6 rounded-md hover:bg-gray-800 text-sm"
          >
            Reset
          </button>
          <button
            onClick={onClose}
            type="button"
            className="bg-[#E7633D] text-white py-2 px-6 rounded-md hover:bg-red-600 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAndPrint}
            type="button"
            className="bg-M-primary-color text-white py-2 px-6 rounded-md hover:bg-blue-700 text-sm"
            disabled={isLoading}
          >
            {loadingAction === "print"
              ? "Saving..."
              : isEditMode
                ? "Update & Print"
                : "Save & Print"}
          </button>
          <button
            type="submit"
            className="bg-M-primary-color text-white py-2 px-6 rounded-md hover:bg-blue-700 text-sm"
            disabled={isLoading}
          >
            {loadingAction === "save"
              ? "Saving..."
              : isEditMode
                ? "Update"
                : "Save"}
          </button>
        </div>
      </form>

      {/* PRINT PREVIEW */}
      {showPrintPreview && printData && (
        <div className="nicu-print-preview">
          <NicuPrintLayout
            className="nicu-print-content"
            title="NICU Admission Form"
            billInfo={printData.billInfo}
          />
        </div>
      )}

      {/* MODALS */}
      <NicuServiceModal
        isOpen={isNewServiceModalOpen}
        onClose={() => setIsNewServiceModalOpen(false)}
        onSave={handleSaveNewService}
      />
      <NicuAgentModal
        isOpen={isNewAgentModalOpen}
        onClose={() => setIsNewAgentModalOpen(false)}
        onSave={handleSaveNewAgent}
      />
      <NicuBedModal
        isOpen={isNewBedModalOpen}
        onClose={() => setIsNewBedModalOpen(false)}
        onSave={handleSaveNewBed}
      />
    </div>
  );
};

export default NicuAdmission;
